import { baseApi } from '@/shared/api/baseApi';
import { cacheBaseKeys } from '@/shared/api/cacheTags';
import type { AuthResponse, User } from '@/shared/api/types';
import { useAppSelector } from '@/shared/model/storeHooks';

// Profile fields to update; an avatar is sent on its own as multipart form data
interface UpdateUserArgs {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  phone_code?: { id?: number };
  avatar?: { media: Blob };
}

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserInfo: builder.query<User, void>({
      query: () => ({
        url: '/user/account/',
        method: 'GET',
      }),
      // The session slice reads the role and onboarding flags from the answer
      providesTags: [cacheBaseKeys.user],
    }),

    updateUserInfo: builder.mutation<User, UpdateUserArgs>({
      query: (data) => {
        if (data.avatar) {
          const form = new FormData();
          form.append('avatar', data.avatar.media);
          return {
            url: '/user/update/',
            method: 'PATCH',
            body: form,
          };
        }
        return {
          url: '/user/update/',
          method: 'PATCH',
          body: data,
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getUserInfo', undefined, (draft) => {
              Object.assign(draft, data);
            }),
          );
        } catch (_error) {}
      },
    }),

    changePassword: builder.mutation<
      { message: { detail: string; type: string } },
      { current: string; password: string }
    >({
      query: ({ current, password }) => {
        const form = new FormData();
        form.append('old_password', current);
        form.append('new_password', password);
        return {
          url: '/user/change_password/',
          method: 'PUT',
          body: form,
        };
      },
      invalidatesTags: [cacheBaseKeys.user],
    }),

    changeEmails: builder.mutation<
      unknown,
      { password: string; new_email: string }
    >({
      query: ({ password, new_email }) => {
        const form = new FormData();
        form.append('password', password);
        form.append('new_email', new_email);
        return {
          url: '/user/change-email/',
          method: 'POST',
          body: form,
        };
      },
    }),

    // Confirms an email change; the answer carries a new token pair that has to be stored
    confirmEmails: builder.query<AuthResponse, { hash: string }>({
      query: ({ hash }) => ({
        url: `/user/change-email/?code=${encodeURIComponent(hash)}`,
        method: 'GET',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // The session slice stores the new tokens from the same answer
          dispatch(
            userApi.util.updateQueryData('getUserInfo', undefined, (draft) => {
              Object.assign(draft, data.user);
            }),
          );
        } catch (_error) {}
      },
    }),

    changeLanguage: builder.mutation<User, { language: string }>({
      query: ({ language }) => ({
        url: '/user/set-language/',
        method: 'POST',
        body: { language },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getUserInfo', undefined, (draft) => {
              Object.assign(draft, data);
            }),
          );
        } catch (_error) {}
      },
    }),
  }),
});

export const {
  useUpdateUserInfoMutation,
  useChangePasswordMutation,
  useChangeEmailsMutation,
  useLazyConfirmEmailsQuery,
  useChangeLanguageMutation,
} = userApi;

type GetUserInfoOptions = Parameters<typeof userApi.useGetUserInfoQuery>[1];

// The profile exists only for a signed-in user, so the query is skipped without
// a token. Otherwise the components that are still mounted during a logout
// refetch it right after the cache reset and get a 401.
export const useGetUserInfoQuery = (
  arg?: void,
  options?: GetUserInfoOptions,
) => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  return userApi.useGetUserInfoQuery(arg, {
    ...options,
    skip: options?.skip || !accessToken,
  });
};
